/**
 * @file ShapesTab.tsx
 * @description Вкладка «Кнопки и карточки» оформления сайта (phase19.16 Р1;
 * SB11, SB73; uiux-design-r1 §1; §7.13 фазы «Отступы»). Высоту кнопок (от
 * 48px) задаёт платформа. Под заголовком группы — строка «Где: …»
 * (`lib/site-view/shape-usage.ts`).
 *
 * SB122 (uiux-settings-groups §5 шаг 3): группы-карточки «Кнопки» ·
 * «Карточки и фото» · «Отступы сайта» (id `spacing`), панель `shapes`; у
 * свёрнутой — сводка выбранных чисел.
 *
 * SB120 этап 2 (uiux-sb120-stage2 §2): всё числами — `ButtonRadiusField`
 * («Пилюля» + px), `CardLookFields` (схемы «Нет / Мягкое / Крупное», два
 * скругления, рамка толщиной и цветом, тень %), `SpacingFields` (плотность
 * множителем и доля телефона). Ползунки — живьём на канве до отпускания.
 *
 * Решение пользователя 2026-10-12 (SB31): «Карточки и фото» и «Каталог →
 * Карточка товара → Вид карточки» — одни поля (`CardLookFields`), разный
 * охват. «!» у групп — из тех же правил, что предупреждения полей. Если у карточки товара свой вид, строка «Где» её не называет, а под
 * ней — «Карточки товаров — свои, заданы в «Каталоге» →».
 */

import { readCatalogOptions, type SiteTheme } from "@chaiberi/shared";
import { useContext, useMemo } from "react";
import { Link } from "react-router";

import { Label } from "../../ui/label";
import { FieldHint, SettingsGroup, SettingsGroups } from "../../ui/settings-groups";
import { Segmented } from "../Segmented";
import { SiteViewContext } from "../site-view-context";
import { ButtonRadiusField } from "./ButtonRadiusField";
import { CardLookFields } from "./CardLookFields";
import { ArticleCardSample, ButtonsSample } from "./ShapeSamples";
import { SpacingFields } from "./SpacingFields";
import { useBuilderLink } from "../../../hooks/site-builder/useBuilderLink";
import { useThemePreview } from "../../../hooks/site-view/useThemePreview";
import { buttonsSummary, DENSITY_RECOMMENDED, outOfRecommended, spacingSummary } from "../../../lib/site-view/appearance-numbers";
import { cardLookText, cardLookWarnings, type CardLookValue } from "../../../lib/site-view/card-shapes";
import { cardsUsageText, shapeUsageText } from "../../../lib/site-view/shape-usage";
import { readDensity } from "../../../lib/site-view/theme-extras";

type Shapes = SiteTheme["shapes"];
type Spacing = SiteTheme["spacing"];

const BUTTON_CASES: ReadonlyArray<{ value: Shapes["buttonCase"]; label: string }> = [
  { value: "asis", label: "Как написано" },
  { value: "upper", label: "ПРОПИСНЫЕ" },
];
const YES_NO: ReadonlyArray<{ value: "yes" | "no"; label: string }> = [
  { value: "yes", label: "Да" },
  { value: "no", label: "Нет" },
];

/** У карточки товара в черновике каталога свой вид («Свои»)? Вне вида сайта — нет. */
function useOwnProductCards(): boolean {
  const view = useContext(SiteViewContext);
  // Живое значение панели каталога (до сохранения), иначе — из черновика.
  const raw = view?.parts?.catalog.fields.options ?? view?.draft?.catalog?.options;
  return useMemo(() => readCatalogOptions(raw).options.card.look.mode === "own", [raw]);
}

/** Вид карточек темы → значение общих полей и обратно. */
const toCardLook = (shapes: Shapes): CardLookValue => ({ radius: shapes.radius.card, field: shapes.radius.field, cards: shapes.cards });
const fromCardLook = (value: CardLookValue, shapes: Shapes): Partial<Shapes> => ({
  radius: { card: value.radius, field: value.field ?? shapes.radius.field },
  cards: value.cards,
});

interface Props {
  theme: SiteTheme;
  onShapes: (patch: Partial<Shapes>) => void;
  onSpacing: (patch: Partial<Spacing>) => void;
}

export function ShapesTab({ theme, onShapes, onSpacing }: Props) {
  const { shapes, colors } = theme;
  const spacing = { ...theme.spacing, density: readDensity(theme) };
  const ownProductCards = useOwnProductCards();
  const link = useBuilderLink();
  const preview = useThemePreview(theme);
  const cardLook = toCardLook(shapes);
  // «!» у групп (SB122) — из тех же правил, что предупреждения под полями.
  const warn = (on: boolean) => (on ? ("warning" as const) : undefined);

  return (
    <SettingsGroups panel="shapes">
      <SettingsGroup id="buttons" title="Кнопки" hint={shapeUsageText("buttons")} summary={buttonsSummary(shapes)}>
        <ButtonRadiusField
          value={shapes.buttonRadius}
          onChange={(buttonRadius) => onShapes({ buttonRadius })}
          onPreview={(buttonRadius) => preview("shapes", { buttonRadius })}
        />
        <div className="flex flex-col gap-2">
          <Label>Стрелка «→» в кнопках-ссылках</Label>
          <Segmented
            aria-label="Стрелка в кнопках-ссылках"
            options={YES_NO}
            value={shapes.buttonArrow ? "yes" : "no"}
            onChange={(v) => onShapes({ buttonArrow: v === "yes" })}
          />
          <FieldHint className="mt-0">{shapeUsageText("buttonArrow")}</FieldHint>
        </div>
        <div className="flex flex-col gap-2">
          <Label>Регистр кнопок</Label>
          <Segmented aria-label="Регистр кнопок" options={BUTTON_CASES} value={shapes.buttonCase} onChange={(buttonCase) => onShapes({ buttonCase })} />
          <FieldHint className="mt-0">{shapeUsageText("buttonCase")}</FieldHint>
        </div>
        <ButtonsSample theme={theme} />
        <FieldHint className="mt-0">Высоту кнопок (не меньше 48px) задаёт платформа.</FieldHint>
      </SettingsGroup>

      <SettingsGroup
        id="cards"
        title="Карточки и фото"
        tocLabel="Карточки"
        hint={cardsUsageText(ownProductCards)}
        summary={cardLookText(cardLook)}
        status={warn(cardLookWarnings(shapes.cards, colors).length > 0)}
      >
        {ownProductCards ? (
          <FieldHint className="mt-0">
            Карточки товаров — свои, заданы в{" "}
            <Link to={link({ panel: "catalog", sub: "card", group: "look" })} className="inline-flex min-h-12 items-center font-medium text-primary hover:underline">
              «Каталоге» →
            </Link>
          </FieldHint>
        ) : null}
        <CardLookFields
          value={cardLook}
          onChange={(value) => onShapes(fromCardLook(value, shapes))}
          onPreview={(value) => preview("shapes", fromCardLook(value, shapes))}
          colors={colors}
          of="карточек"
          radiusLabel="Карточки и фото"
          sample={<ArticleCardSample theme={theme} />}
        />
      </SettingsGroup>

      <SettingsGroup
        id="spacing"
        title="Отступы сайта"
        tocLabel="Отступы"
        hint={shapeUsageText("spacing")}
        summary={spacingSummary(spacing)}
        status={warn(outOfRecommended(spacing.density, DENSITY_RECOMMENDED))}
      >
        <SpacingFields spacing={spacing} onSpacing={onSpacing} onPreview={(patch) => preview("spacing", patch)} />
      </SettingsGroup>
    </SettingsGroups>
  );
}
